/**
 * React Query hook for the HR Directory tool.
 *
 * Reads GET /v1/core/directory, which now serves the `employee` table loaded
 * from Rippling rather than a live Microsoft Graph and Google Workspace merge.
 * That change is what lets the directory show store staff at all (most clerks
 * share one Google account per store, so Graph and Google could never see them)
 * and what gives it a reporting line (the manager chain exists only in Rippling).
 *
 * One payload, no server paging. The tree, the department grouping and all
 * filtering happen client-side inside the shared EmployeeDirectory component.
 *
 * Graceful degradation: if the endpoint is not yet deployed (404) or the roster
 * has not been loaded on this stage (503), the hook surfaces the error and the
 * page renders a quiet "not available yet" placeholder rather than crashing.
 */

import { useQuery } from '@tanstack/react-query';
import type { DirectoryPerson, DirectoryStore } from '@ampersandbrands/ui';
import { apiGet, type ApiError } from './api';
import { API_BASE } from './apiBase';

const CORE_API_BASE = `${API_BASE}/v1/core`;

export type { DirectoryPerson, DirectoryStore };

type DirectoryResponse = {
  people: DirectoryPerson[];
  stores: DirectoryStore[];
  corporate_phone: string | null;
  synced_at: string | null;
};

export function isNotAvailable(error: unknown): boolean {
  const status = (error as ApiError | null)?.status;
  return status === 404 || status === 503;
}

export function useDirectory() {
  const query = useQuery<DirectoryResponse, Error>({
    queryKey: ['hr', 'directory'],
    queryFn: () => apiGet<DirectoryResponse>(`${CORE_API_BASE}/directory`),
    staleTime: 60_000,
    retry: false,
  });
  return {
    people: query.data?.people ?? [],
    stores: query.data?.stores ?? [],
    corporatePhone: query.data?.corporate_phone ?? null,
    syncedAt: query.data?.synced_at ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
